import { SafePlugin } from '@atlaskit/editor-common/safe-plugin';
import type { Node as PMNode } from '@atlaskit/editor-prosemirror/model';
import {
	PluginKey,
	type EditorState,
	type ReadonlyTransaction,
} from '@atlaskit/editor-prosemirror/state';
import { Decoration, DecorationSet } from '@atlaskit/editor-prosemirror/view';

import { aiSuggestionsPluginKey, type AiSuggestionsMeta } from './main';

const acceptShimmerPluginKey = new PluginKey<AcceptShimmerState>('aiSuggestionsAcceptShimmer');

const SHIMMER_FALLBACK_DURATION_MS = 1300;
const SHIMMER_MS_PER_PIXEL = 0.6;
const SHIMMER_MIN_DURATION_MS = 800;
const SHIMMER_MAX_DURATION_MS = 1600;

type AcceptShimmerMeta = { action: 'CLEAR' } | { action: 'SET_DURATION'; duration: number };

type AcceptShimmerState = {
	decorations: DecorationSet;
	newSuggestionId?: string;
	shimmerDuration: number;
};

export const getAcceptShimmerDuration = (state: EditorState): number =>
	acceptShimmerPluginKey.getState(state)?.shimmerDuration ?? 0;

const getShimmerDurationForWidth = (width: number | undefined): number => {
	if (width === undefined) {
		return SHIMMER_FALLBACK_DURATION_MS;
	}
	return Math.min(
		SHIMMER_MAX_DURATION_MS,
		Math.round(SHIMMER_MIN_DURATION_MS + width * SHIMMER_MS_PER_PIXEL),
	);
};

/** Map each step's inserted/replaced range into the final document before finding text blocks. */
const getAcceptedTextBlockDecorations = (tr: ReadonlyTransaction, doc: PMNode): DecorationSet => {
	const decorations: Decoration[] = [];
	const seenPositions = new Set<number>();

	tr.mapping.maps.forEach((stepMap, stepIndex) => {
		stepMap.forEach((_oldStart, _oldEnd, newStart, newEnd) => {
			if (newStart === newEnd) {
				return;
			}

			const remainingMapping = tr.mapping.slice(stepIndex + 1);
			const from = remainingMapping.map(newStart, 1);
			const to = remainingMapping.map(newEnd, -1);
			if (from >= to) {
				return;
			}

			doc.nodesBetween(from, to, (node, pos) => {
				if (node.isTextblock && node.textContent && !seenPositions.has(pos)) {
					seenPositions.add(pos);
					decorations.push(
						Decoration.inline(pos + 1, pos + node.nodeSize - 1, {
							class: 'ai-suggestions-accept-shimmer-background',
						}),
					);
				}
			});
		});
	});

	return decorations.length > 0 ? DecorationSet.create(doc, decorations) : DecorationSet.empty;
};

const setSweepEdges = (editorDom: HTMLElement): number | undefined => {
	let left = Infinity;
	let right = -Infinity;
	for (const block of editorDom.querySelectorAll('.ai-suggestions-accept-shimmer-background')) {
		const range = editorDom.ownerDocument.createRange();
		range.selectNodeContents(block);
		const rects = range.getClientRects();
		for (let i = 0; i < rects.length; i++) {
			const rect = rects[i];
			if (rect.width > 0 && rect.height > 0) {
				left = Math.min(left, rect.left);
				right = Math.max(right, rect.right);
			}
		}
	}
	if (Number.isFinite(left) && Number.isFinite(right)) {
		editorDom.style.setProperty('--ai-accept-shimmer-left-edge', `${left}px`);
		editorDom.style.setProperty('--ai-accept-shimmer-right-edge', `${right}px`);
		return right - left;
	}
	return undefined;
};

/** Adds a temporary decoration when an AI suggestion edits a text block. */
export const createAcceptShimmerPlugin = (): SafePlugin<AcceptShimmerState> => {
	return new SafePlugin<AcceptShimmerState>({
		key: acceptShimmerPluginKey,
		state: {
			init: (): AcceptShimmerState => ({
				decorations: DecorationSet.empty,
				shimmerDuration: 0,
			}),
			apply(tr: ReadonlyTransaction, state: AcceptShimmerState, _oldState, newState: EditorState) {
				const shimmerMeta = tr.getMeta(acceptShimmerPluginKey) as AcceptShimmerMeta | undefined;
				const meta = tr.getMeta(aiSuggestionsPluginKey) as AiSuggestionsMeta | undefined;
				if (shimmerMeta?.action === 'CLEAR') {
					return { ...state, decorations: DecorationSet.empty, shimmerDuration: 0 };
				}
				if (shimmerMeta?.action === 'SET_DURATION') {
					return { ...state, shimmerDuration: shimmerMeta.duration };
				}
				if (meta?.action === 'ACCEPT_SUGGESTION') {
					const decorations = getAcceptedTextBlockDecorations(tr, newState.doc);
					return {
						decorations,
						newSuggestionId: meta.suggestion.id,
						shimmerDuration: decorations === DecorationSet.empty ? 0 : SHIMMER_FALLBACK_DURATION_MS,
					};
				}

				if (!tr.docChanged) {
					return state;
				}

				const decorations = state.decorations.map(tr.mapping, newState.doc);
				return {
					...state,
					decorations,
					shimmerDuration: decorations === DecorationSet.empty ? 0 : state.shimmerDuration,
				};
			},
		},
		props: {
			decorations(editorState: EditorState) {
				return acceptShimmerPluginKey.getState(editorState)?.decorations ?? DecorationSet.empty;
			},
		},
		view: (editorView) => {
			let timeout: ReturnType<typeof setTimeout> | undefined;
			let lastSuggestionId: string | undefined;
			const clearSweepEdges = () => {
				editorView.dom.style.removeProperty('--ai-accept-shimmer-left-edge');
				editorView.dom.style.removeProperty('--ai-accept-shimmer-right-edge');
				editorView.dom.style.removeProperty('--ai-accept-shimmer-duration');
			};

			return {
				update() {
					const state = acceptShimmerPluginKey.getState(editorView.state);
					if (!state || state.newSuggestionId === lastSuggestionId) {
						return;
					}
					lastSuggestionId = state.newSuggestionId;
					if (timeout) {
						clearTimeout(timeout);
					}
					clearSweepEdges();
					if (state.decorations === DecorationSet.empty) {
						return;
					}
					const width = setSweepEdges(editorView.dom);
					const duration = getShimmerDurationForWidth(width);

					editorView.dom.style.setProperty('--ai-accept-shimmer-duration', `${duration}ms`);
					timeout = setTimeout(() => {
						editorView.dispatch(
							editorView.state.tr
								.setMeta(acceptShimmerPluginKey, { action: 'CLEAR' })
								.setMeta('addToHistory', false),
						);
						clearSweepEdges();
					}, duration);

					editorView.dispatch(
						editorView.state.tr
							.setMeta(acceptShimmerPluginKey, { action: 'SET_DURATION', duration })
							.setMeta('addToHistory', false),
					);
				},
				destroy() {
					if (timeout) {
						clearTimeout(timeout);
					}
					clearSweepEdges();
				},
			};
		},
	});
};
